import React, { useId, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeleteConfirmDialog } from "src/domains/shared/components/DeleteConfirmDialog";
import { Button, ButtonProps } from "../Button";
import { EraserIcon } from "../../../constants/icons/EraserIcon";
import { useFeatureStore } from "../../../hooks";
import { InnerButton } from "../InnerButton";
import { useFeaturesStore } from "../../../FeaturesStore";
import { FeatureFromGeojson } from "../../../FeaturesStore";
import { useFeatureFinder } from "../../../hooks/incontext/useFeatureFinder";

import { MAP_TOOL_BUTTON } from "src/domains/shared/constants/mapToolButton";

export interface DeleteAnnotationProps extends ButtonProps {
  onDeleteChange?: (e: FeatureFromGeojson | undefined) => void;
}

export const DeleteAnnotation = ({
  onDeleteChange,
  disabled = false,
  ...props
}: DeleteAnnotationProps) => {
  const { t } = useTranslation("timeSeries");
  const { selectedFeature } = useFeatureStore();
  const { removeGeoJson, getGeoJsonElement } = useFeaturesStore();
  const { findFeatureById, collectAllVectorSources } = useFeatureFinder();

  const id = useId();
  const buttonId = `controlbutton-${id}`;
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleDelete = useCallback(() => {
    if (!selectedFeature?.id) return;

    const featureId = String(selectedFeature.id);
    const feature = findFeatureById(featureId);

    if (feature) {
      const cachedSource = feature.get("source");
      if (cachedSource && typeof cachedSource.removeFeature === "function") {
        cachedSource.removeFeature(feature);
      } else {
        const allSources = collectAllVectorSources();
        for (const src of allSources) {
          if (src.getFeatureById(featureId) === feature) {
            src.removeFeature(feature);
            break;
          }
        }
      }
    }

    const deletedGeoJson = getGeoJsonElement(featureId);
    removeGeoJson(featureId);
    onDeleteChange?.(deletedGeoJson);
  }, [
    selectedFeature,
    findFeatureById,
    collectAllVectorSources,
    removeGeoJson,
    onDeleteChange,
    getGeoJsonElement,
  ]);

  const isButtonDisabled = disabled || !selectedFeature?.id;
  const selectedTitle = String(selectedFeature?.properties?.title ?? "");

  const confirmDelete = () => {
    setIsConfirmOpen(false);
    handleDelete();
  };

  return (
    <>
      <Button
        id={buttonId}
        isDisabled={isButtonDisabled}
        disabled={isButtonDisabled}
        hasPopup
        popupText={t("mapTool.tool.delete")}
        onClick={() => setIsConfirmOpen(true)}
        isActive={false}
        {...props}
      >
        <InnerButton isActive={false}>
          <EraserIcon size={MAP_TOOL_BUTTON.ICON_PX} color={MAP_TOOL_BUTTON.idleIcon} />
        </InnerButton>
      </Button>
      <DeleteConfirmDialog
        open={isConfirmOpen}
        title={t("deleteModal.title")}
        message={t("mapTool.deleteConfirm.subtitle", { name: selectedTitle })}
        subMessage={t("mapTool.deleteConfirm.irreversible")}
        cancelLabel={t("mapTool.deleteConfirm.cancel")}
        confirmLabel={t("deleteModal.delete")}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </>
  );
};
